'use client';

/**
 * **The visible way to finish an edit, on any composite popover of this
 * treatment** (VALE-1710), **and the visible way to abandon one** (VALE-2080).
 *
 * Every gesture these panels offer already commits — dismissing does, `Enter`
 * does — and not one of them was on screen. A reader who had just typed an
 * address had nothing telling them how to finish, and the worry is the opposite
 * of the truth: that clicking away *discards* the typing rather than saving it.
 *
 * **Its own module, because there are two shapes of member and they must not
 * drift apart.** Most panels bind `useAttributePopoverCommit`, which returns
 * this already wired ({@link useAttributePopoverCommit.confirm}) — those should
 * render that, never reach for this directly. The exception is a panel that
 * hand-writes its `Popover` and owns its own commit lifecycle: `Other names`
 * and `Other addresses` are repeating collections with per-row create/update/
 * delete mutations, so the shared contract does not fit them and their host
 * commits on dismiss itself. They render this component directly, and the point
 * of the shared module is that the word, the size and the corner are then the
 * same on every panel whichever route it arrived by.
 *
 * **Cancel is part of the same component, and `onCancel` is required**, so a
 * panel cannot show Done without Cancel beside it, whichever route it arrived
 * by. Before VALE-2080 the only discard was `Escape`, which nothing on screen
 * mentioned. Cancel does exactly what `Escape` does on that panel, and clicking
 * outside still commits: see {@link ATTRIBUTE_POPOVER_CANCEL_LABEL}.
 */

import { Button } from '@/components/ui/button';
import {
  ATTRIBUTE_POPOVER_CANCEL_LABEL,
  ATTRIBUTE_POPOVER_CONFIRM_LABEL,
  ATTRIBUTE_POPOVER_CONFIRM_ROW,
  ATTRIBUTE_POPOVER_CONFIRM_ROW_INSET
} from './attribute-tokens';

export function AttributePopoverConfirm({
  onConfirm,
  onCancel,
  inset = false,
  disabled = false,
  cancelDisabled = false
}: {
  /**
   * **Must be the panel's own commit path, not a second one.** On a hook-bound
   * member that is the shared `onCommit`, so `validate` gets its say and a
   * refused commit leaves the panel open with the typing intact. On a
   * hand-written member it is whatever that panel's `Enter` already does.
   */
  onConfirm: () => void;
  /**
   * **Must be the panel's own revert path, the one `Escape` takes.** On a
   * hook-bound member that is the shared revert, which restores the value the
   * panel opened with, across every part, and closes without saving. On a
   * hand-written member it is whatever that panel's `Escape` already does.
   */
  onCancel: () => void;
  /**
   * **The panel supplies no padding of its own**, so this row carries it — see
   * {@link ATTRIBUTE_POPOVER_CONFIRM_ROW_INSET}. One panel is in that state
   * today: `multiSelect`, whose `Command` list brings its own padding.
   */
  inset?: boolean;
  /**
   * **Only where the panel knows its draft is incomplete before committing it**
   * — the identity-variant forms, whose `canSubmit` gates their `Enter` too, so
   * a live button here would be a control that visibly does nothing.
   *
   * Hook-bound members never pass this: their refusal comes from `validate` at
   * commit time and is *meant* to be reachable, since a refusal is how the
   * reader is told what is wrong.
   */
  disabled?: boolean;
  /**
   * **Only while a hand-written member's save is in flight**, when abandoning
   * the draft can no longer stop the write. Not tied to {@link disabled}: an
   * incomplete draft is exactly the one a reader may want to throw away.
   */
  cancelDisabled?: boolean;
}) {
  return (
    <div
      className={
        inset
          ? ATTRIBUTE_POPOVER_CONFIRM_ROW_INSET
          : ATTRIBUTE_POPOVER_CONFIRM_ROW
      }
    >
      <Button
        type="button"
        variant="ghost"
        size="2xs"
        density="micro"
        disabled={cancelDisabled}
        onClick={onCancel}
      >
        {ATTRIBUTE_POPOVER_CANCEL_LABEL}
      </Button>
      <Button
        type="button"
        size="2xs"
        density="micro"
        disabled={disabled}
        onClick={onConfirm}
      >
        {ATTRIBUTE_POPOVER_CONFIRM_LABEL}
      </Button>
    </div>
  );
}
